/**
 * Download my data — POPIA data-portability self-service screen (web port).
 *
 * Web counterpart of apps/mobile/app/data-export.tsx. Gives the signed-in user
 * a copy of every record the platform holds about them (their POPIA right to
 * data portability). Nothing is fetched on mount: the user presses "Prepare my
 * data", which lazily calls GET /me/export (scoped to their bearer token via
 * the fseAxios interceptor — no user-id param). On success we show a short
 * summary of what was gathered, then offer a "Download JSON" action (an
 * in-browser Blob download — no native deps) plus an inline, read-only,
 * selectable monospace view of the full JSON so they can copy it directly. A
 * "Refresh" re-runs the export.
 *
 * Lazy fetch uses useMutation for explicit-trigger semantics (mutate()) plus
 * isPending / isError / data for free.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me-export.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Icon,
  ListItem,
  Paper,
  Stack,
  StatusPill,
  Text,
} from '@fse/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { meControllerExport } from '../api/generated/me/me';
import type { MyExportDto } from '../api/generated/models';

/**
 * Trigger an in-browser download of `data` as a pretty-printed JSON file.
 * Builds a Blob, hangs it off a temporary <a download>, clicks it, then revokes
 * the object URL. Guarded for non-browser contexts (SSR / tests) where neither
 * window nor the DOM exist.
 */
function downloadJson(data: unknown, filename: string): void {
  if (typeof window === 'undefined') return;
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function MyDataScreen() {
  const { t } = useTranslation();
  // Lazy: nothing is fetched on mount. The export is only gathered when the
  // user presses "Prepare my data" (or "Refresh").
  const exportMutation = useMutation({
    mutationFn: () => meControllerExport(),
  });

  const data: MyExportDto | undefined = exportMutation.data;
  const isPreparing = exportMutation.isPending;
  const errorMessage =
    exportMutation.error instanceof FseApiError
      ? exportMutation.error.message
      : exportMutation.error
        ? String(exportMutation.error)
        : null;

  // Pretty-printed once per render — reused for the inline view and the
  // download payload so they're always identical.
  const prettyJson = data ? JSON.stringify(data, null, 2) : '';

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Text variant="headline-medium" as="h1">
          {t('myData.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('myData.subtitle')}
          </Text>
        </Box>
      </Box>

      {!data && (
        <Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => exportMutation.mutate()}
            disabled={isPreparing}
            startIcon={isPreparing ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isPreparing ? t('myData.preparing') : t('myData.prepare')}
          </Button>
        </Box>
      )}

      {exportMutation.isError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => exportMutation.mutate()}
              disabled={isPreparing}
            >
              {t('myData.retry')}
            </Button>
          }
        >
          {t('myData.prepareError', { error: errorMessage })}
        </Alert>
      )}

      {data && (
        <>
          <Card>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text variant="title-medium" as="h2">
                  {t('myData.ready')}
                </Text>
                <StatusPill variant="active" icon="check_circle">
                  {t('myData.ready')}
                </StatusPill>
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <ListItem
                  leadingIcon="agriculture"
                  title={t('myData.farmProfile')}
                  trailing={
                    <Text variant="body-medium" color="text.primary">
                      {data.farmProfile != null ? t('myData.yes') : t('myData.no')}
                    </Text>
                  }
                />
                <ListItem
                  leadingIcon="inventory_2"
                  title={t('myData.contributions')}
                  trailing={
                    <Text variant="body-medium" color="text.primary">
                      {String(data.contributions.length)}
                    </Text>
                  }
                />
                <ListItem
                  leadingIcon="history"
                  title={t('myData.auditLogEntries')}
                  trailing={
                    <Text variant="body-medium" color="text.primary">
                      {String(data.auditLog.length)}
                    </Text>
                  }
                  noDivider
                />
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => downloadJson(data, 'fse-my-data.json')}
              startIcon={<Icon name="download" size={20} />}
            >
              {t('myData.downloadJson')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => exportMutation.mutate()}
              disabled={isPreparing}
              startIcon={isPreparing ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {isPreparing ? t('myData.refreshing') : t('myData.refresh')}
            </Button>
          </Box>

          <Box>
            <Box sx={{ mb: 1 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('myData.fullExport')}
              </Text>
            </Box>
            <Paper
              variant="outlined"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'auto',
                maxHeight: 480,
                overflowY: 'auto',
                p: 2,
                m: 0,
                userSelect: 'text',
              }}
            >
              {prettyJson}
            </Paper>
          </Box>
        </>
      )}
    </Stack>
  );
}
